import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { db } from '../lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { ThemeConfig } from '../admin/components/builder/types';

type Theme = 'dark' | 'light';

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  globalConfig: ThemeConfig | null;
}

const ThemeProviderContext = createContext<ThemeProviderState | undefined>(undefined);

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
  );
  const [globalConfig, setGlobalConfig] = useState<ThemeConfig | null>(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'theme'), (doc) => {
       if (doc.exists() && doc.data().theme) {
          setGlobalConfig(doc.data().theme);
       }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    
    // Override with global config if forced dark mode
    if (globalConfig?.darkMode) {
       root.classList.add('dark');
       setTheme('dark');
    } else {
       root.classList.add(theme);
    }
    
    if (globalConfig) {
       root.style.setProperty('--primary', globalConfig.primaryColor);
       root.style.setProperty('--secondary', globalConfig.secondaryColor);
       root.style.setProperty('--bg-color', globalConfig.backgroundColor);
       
       if (globalConfig.typography === 'noto') root.classList.add('font-noto');
       else root.classList.remove('font-noto');
    }
  }, [theme, globalConfig]);

  const value = {
    theme,
    globalConfig,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
    toggleTheme: () => {
      const newTheme = theme === 'dark' ? 'light' : 'dark';
      localStorage.setItem(storageKey, newTheme);
      setTheme(newTheme);
    }
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};
